import React, { useState, useMemo, Suspense, lazy } from 'react';
import type { User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import {
  Copy,
  Check,
  Trophy,
  Search,
  MessageCircle,
  AlertCircle,
  QrCode,
  Camera,
  ScanLine,
  ShoppingBag,
  ChevronRight,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';
import { Block } from '../components/ui/Block';
import { StickerChip } from '../components/ui/StickerChip';
import { Toast } from '../components/ui/Toast';
import { MyPrioritiesSection } from '../components/MyPrioritiesSection';
import { isPayloadFresh, type QrPayload } from '../lib/qrPayload';
import type { TabKey } from '../components/layout/Tabs';
import { useTutorialTarget } from '../components/tutorial/useTutorialTarget';

const QrShowModal = lazy(() =>
  import('../components/qr/QrShowModal').then((m) => ({ default: m.QrShowModal }))
);
const QrScanModal = lazy(() =>
  import('../components/qr/QrScanModal').then((m) => ({ default: m.QrScanModal }))
);

interface Props {
  user: User;
  counts: Record<string, number>;
  allCodes: string[];
  onChangeTab?: (tab: TabKey) => void;
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button
      type="button"
      variant={copied ? 'primary' : 'secondary'}
      size="sm"
      icon={copied ? Check : Copy}
      onClick={handle}
    >
      {copied ? 'Copiado!' : label}
    </Button>
  );
}

export function TrocasTab({ user, counts, allCodes, onChangeTab }: Props) {
  const navigate = useNavigate();
  const [friendInput, setFriendInput] = useState('');
  const [mode, setMode] = useState<'closed' | 'show' | 'scan'>('closed');
  const [scanError, setScanError] = useState<string | null>(null);
  const qrSectionRef = useTutorialTarget('trocas-qr-buttons');
  const prioritiesRef = useTutorialTarget('trocas-prioridades');

  const validCodesSet = useMemo(() => new Set(allCodes), [allCodes]);

  const myDuplicates = useMemo(
    () => allCodes.filter((c) => (counts[c] || 0) >= 2),
    [counts, allCodes]
  );

  const myMissing = useMemo(
    () => allCodes.filter((c) => (counts[c] || 0) === 0),
    [counts, allCodes]
  );

  const myMissingSet = useMemo(() => new Set(myMissing), [myMissing]);

  const duplicatesFormatted = myDuplicates
    .map((c) => {
      const extras = (counts[c] || 0) - 1;
      return extras > 1 ? `${c} x${extras}` : c;
    })
    .join(', ');

  const duplicatesCopyText = `*Minhas repetidas (BoraTrocar - Copa 2026):*\n${
    duplicatesFormatted || 'Nenhuma ainda'
  }`;

  const friendCodes = useMemo(() => {
    const matches = friendInput.toUpperCase().match(/[A-Z]{2,3}\d{1,2}/g) || [];
    return Array.from(new Set(matches.filter((c) => validCodesSet.has(c))));
  }, [friendInput, validCodesSet]);

  const matchesForMe = useMemo(
    () => friendCodes.filter((c) => myMissingSet.has(c)),
    [friendCodes, myMissingSet]
  );

  const handleShowQr = () => setMode('show');
  const handleScanQr = () => setMode('scan');

  const handleScanResult = async (payload: QrPayload) => {
    setMode('closed');
    if (!isPayloadFresh(payload)) {
      setScanError('Esse QR expirou. Peça um novo.');
      return;
    }
    if (payload.u === user.id) {
      setScanError('Esse é o seu próprio QR.');
      return;
    }
    const { data, error } = await supabase.rpc('start_trade', { p_other: payload.u });
    if (error || !data) {
      setScanError('Não foi possível iniciar a troca. Tente novamente.');
      return;
    }
    navigate(`/troca/${data}`);
  };

  const handleCloseModals = () => setMode('closed');

  return (
    <div className="pb-4">
      <header className="mb-6">
        <p className="bt-eyebrow text-green-700 mb-2">Trocas</p>
        <h1 className="font-display font-bold text-h1 text-ink-900 tracking-[-0.015em]">
          Bora trocar a galera.
        </h1>
        <p className="font-body text-body text-ink-700 mt-2">
          Compartilhe as suas repetidas e faltantes ou cole a lista do amigo aqui pra cruzar.
        </p>
      </header>

      <div ref={prioritiesRef}>
        <MyPrioritiesSection userId={user.id} />
      </div>

      <section
        ref={qrSectionRef}
        className="bg-white border border-ink-300 rounded-xl mb-4 overflow-hidden shadow-xs"
      >
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <ScanLine size={18} className="text-green-700" strokeWidth={2.5} />
            <h2 className="font-display font-semibold text-h3 text-ink-900">
              Trocar pessoalmente
            </h2>
          </div>
          <p className="text-small text-ink-700 mb-3">
            Tá com a galera? Use o QR Code pra fazer o match na hora!
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="primary"
              size="md"
              fullWidth
              icon={QrCode}
              onClick={handleShowQr}
            >
              Mostrar meu QR
            </Button>
            <Button
              variant="secondary"
              size="md"
              fullWidth
              icon={Camera}
              onClick={handleScanQr}
            >
              Ler QR de amigo
            </Button>
          </div>
        </div>
      </section>

      <Block
        title="Minhas repetidas"
        count={myDuplicates.length}
        countTone="gold"
        defaultOpen={myDuplicates.length > 0}
        headerRight={
          myDuplicates.length > 0 ? (
            <CopyButton text={duplicatesCopyText} label="Copiar" />
          ) : undefined
        }
      >
        {myDuplicates.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {myDuplicates.map((c) => (
              <StickerChip key={c} code={c} tone="gold" count={(counts[c] || 0) - 1} />
            ))}
          </div>
        ) : (
          <p className="text-small text-ink-500 italic">
            Nenhuma repetida ainda. Bora marcar mais.
          </p>
        )}
      </Block>

      <Block
        title="Minhas faltantes"
        count={myMissing.length}
        countTone="ruby"
        headerRight={
          myMissing.length > 0 ? (
            <CopyButton
              text={`*Minhas faltantes (BoraTrocar - Copa 2026):*\n${myMissing.join(', ')}`}
              label="Copiar"
            />
          ) : undefined
        }
      >
        {myMissing.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {myMissing.map((c) => (
              <StickerChip key={c} code={c} tone="ink" />
            ))}
          </div>
        ) : (
          <p className="inline-flex items-center gap-2 text-small font-semibold text-green-700">
            <Trophy size={16} strokeWidth={2.5} />
            Álbum completo. Parabéns.
          </p>
        )}
      </Block>

      {myMissing.length > 0 && onChangeTab && (
        <button
          type="button"
          onClick={() => onChangeTab('parceiros')}
          className="w-full mb-4 rounded-xl border-[1.5px] border-gold-300 bg-gold-100 px-4 py-3 flex items-center gap-3 btn-focus hover:bg-gold-100/70 transition-colors text-left"
        >
          <span className="w-10 h-10 rounded-md bg-white inline-flex items-center justify-center flex-shrink-0">
            <ShoppingBag size={20} strokeWidth={2.25} className="text-green-700" />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block font-display font-bold text-[14px] text-ink-900">
              Tem {myMissing.length} faltantes?
            </span>
            <span className="block text-[11px] text-ink-700 mt-0.5">
              Ver bundles de figurinhas
            </span>
          </span>
          <ChevronRight size={16} strokeWidth={2.25} className="text-ink-700" />
        </button>
      )}

      <Block
        title="Verificar lista de amigo"
        defaultOpen
        description="Cola as repetidas do seu amigo (ex: BRA1, ARG5 x2, FWC23). A gente cruza com suas faltantes."
      >
        <textarea
          value={friendInput}
          onChange={(e) => setFriendInput(e.target.value.slice(0, 5000))}
          maxLength={5000}
          placeholder="Cole aqui as figurinhas repetidas do seu amigo..."
          className="w-full h-28 text-small text-ink-900 placeholder:text-ink-500 border border-ink-300 bg-white rounded-lg p-3 resize-none transition-[border-color,box-shadow] focus:outline-none focus:border-green-700 focus-visible:shadow-focus-ring font-mono"
        />
        {friendInput && friendCodes.length === 0 && (
          <div className="mt-3 p-3 rounded-lg border border-ink-300 bg-ink-100 inline-flex items-center gap-2 text-small text-ink-700">
            <Search size={16} className="text-ink-500" />
            Nenhum código válido detectado. Verifique o formato (ex: BRA10).
          </div>
        )}
        {friendCodes.length > 0 && (
          <div
            className={[
              'mt-3 p-4 rounded-lg border',
              matchesForMe.length > 0
                ? 'border-green-700 bg-green-100'
                : 'border-ink-300 bg-ink-100',
            ].join(' ')}
          >
            <div className="flex items-center gap-2 mb-2">
              {matchesForMe.length > 0 ? (
                <>
                  <MessageCircle size={18} strokeWidth={2.25} className="text-green-700" />
                  <p className="font-display font-semibold text-h3 text-green-900">
                    Match. Você precisa de {matchesForMe.length} figurinha(s):
                  </p>
                </>
              ) : (
                <>
                  <AlertCircle size={18} strokeWidth={2.25} className="text-ink-500" />
                  <p className="font-display font-semibold text-h3 text-ink-700">
                    Nenhuma figurinha dele que você precisa.
                  </p>
                </>
              )}
            </div>
            {matchesForMe.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {matchesForMe.map((c) => (
                  <StickerChip key={c} code={c} tone="green" />
                ))}
              </div>
            )}
            <p className="text-caption text-ink-500 mt-3">
              {friendCodes.length} código(s) detectado(s) na lista do amigo.
            </p>
          </div>
        )}
      </Block>

      <Suspense fallback={null}>
        {mode === 'show' && <QrShowModal user={user} onClose={handleCloseModals} />}
        {mode === 'scan' && (
          <QrScanModal onScan={handleScanResult} onClose={handleCloseModals} />
        )}
      </Suspense>

      {scanError && <Toast message={scanError} onClose={() => setScanError(null)} />}
    </div>
  );
}
